/**
 * OrgTreeEditor (S2.1, rbacV2) — the 組織 hierarchy tree: the Axis-B spine for
 * node-scoped assignment (S2.4 anchors org-scope picks on these nodes).
 *
 * Follows the tree-first interaction grammar of the category
 * `TaxonomyTreeEditor` / `CreateNodeModal` pair (holistic category pattern —
 * NOT the flat party-category components): controlled AntD Tree, inline
 * add-child on the parent node (the host opens the 3-field create modal with
 * the parent preset), drag re-parent surfaced as an intent so the host collects
 * the mandatory reason first (ReasonRequiredModal → audit row), inactive gray.
 * Re-parent has TWO equivalent paths into the same `onMove` intent: drag (the
 * pointer power-path) and the node ⋯ menu's 變更上層 Change-parent modal (the
 * keyboard/SR-reachable, discoverable path — ux-review: no drag-only
 * capability). Both share the same guards: cycle candidates and inactive
 * parents are FILTERED out of the modal's options (hide-don't-disable), and
 * the drop handler re-checks.
 * Verbatim embedding of `TaxonomyTreeEditor` was not possible without editing
 * that shared component (it is hard-typed to `ProductCategory[]` and has no
 * title-decorator seam for the org badges below); a follow-up genericizes it so
 * this editor and the S2.4 scope picker converge on one component.
 *
 * Per-node scope signals (each a glyph + full-word label + own tooltip — never
 * a bare glyph, never colour-only):
 *  - 直營/加盟 operating-model badge (parity with the flat table).
 *  - `model === 'franchise'` → scope-BOUNDARY flag: the compartment is closed
 *    from above (Axis-B descent-stop unless `crossFranchiseView`).
 *  - non-root nodes inside an open compartment → INHERITED-scope indicator
 *    naming the compartment root their data scope descends from.
 *
 * Mutations are NOT performed here — add-child / re-parent / status-toggle all
 * surface to the host (`OrganisationsTab`), which owns the reason gate and the
 * cache invalidation. Selection is deliberately absent in S2.1 (no per-node
 * route/detail surface exists yet — route-derived selection arrives with the
 * S2.4 picker consumer).
 */

import { useEffect, useMemo, useState } from 'react';
import {
  App,
  Button,
  Dropdown,
  Modal,
  Select,
  Spin,
  Tag,
  Tooltip,
  Tree,
  Typography,
  theme,
} from 'antd';
import type { TreeDataNode, TreeProps } from 'antd';
import {
  MoreOutlined,
  NodeIndexOutlined,
  PartitionOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens } from '@/styles/tokens';
import type { OrganisationUnit } from './model';

const { Text } = Typography;

/** A drag re-parent landed and passed the client guards — host collects the reason. */
export interface OrgMoveIntent {
  nodeId: string;
  /** `null` = move to top level (become a root). */
  newParentId: string | null;
}

export interface OrgTreeEditorProps {
  orgs: OrganisationUnit[];
  /**
   * The host fetch's in-flight state — same contract the flag-OFF
   * `<Table loading>` carries, so a pending load renders a Spin (never the
   * "no organisations" empty state; ux-review: blank reads as broken).
   */
  loading?: boolean;
  /** Inline add-child affordance — host opens the create modal with this parent preset. */
  onAddChild: (parentId: string) => void;
  /** Drag re-parent passed the client guards — host collects reason + mutates (server re-checks). */
  onMove: (intent: OrgMoveIntent) => void;
  /** Activate/deactivate — host runs the reason-gated status flow. */
  onToggleStatus: (org: OrganisationUnit) => void;
}

/** All ids in `rootId`'s subtree (self included) — client-side cycle guard. */
function orgSubtreeIds(orgs: readonly OrganisationUnit[], rootId: string): Set<string> {
  const ids = new Set<string>([rootId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const org of orgs) {
      if (org.parentId && ids.has(org.parentId) && !ids.has(org.id)) {
        ids.add(org.id);
        grew = true;
      }
    }
  }
  return ids;
}

/**
 * The compartment root a node's data scope descends from: the highest ancestor
 * reachable without crossing a franchise boundary (mirrors the Axis-B resolver's
 * descent-stop, `src/lib/rbac/orgScope.ts`). `null` for roots and for franchise
 * nodes — a franchise node IS its own compartment root (boundary flag instead).
 */
function scopeOriginOf(
  org: OrganisationUnit,
  byId: Map<string, OrganisationUnit>
): OrganisationUnit | null {
  if (org.model === 'franchise') return null;
  let cur = org;
  const seen = new Set<string>([org.id]);
  while (cur.parentId) {
    const parent = byId.get(cur.parentId);
    if (!parent || seen.has(parent.id)) break; // defensive cycle guard
    seen.add(parent.id);
    cur = parent;
    if (cur.model === 'franchise') break; // boundary: scope cannot descend from above it
  }
  return cur.id === org.id ? null : cur;
}

const OrgTreeEditor = ({ orgs, loading, onAddChild, onMove, onToggleStatus }: OrgTreeEditorProps) => {
  const { t } = useTranslation('governance');
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const formStyles = useFormStyles();

  const byId = useMemo(() => new Map(orgs.map((o) => [o.id, o])), [orgs]);

  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [autoExpandSeeded, setAutoExpandSeeded] = useState(false);
  /** The 變更上層 Change-parent modal target + its draft parent (`null` = root). */
  const [reparentTarget, setReparentTarget] = useState<OrganisationUnit | null>(null);
  const [reparentDraft, setReparentDraft] = useState<string | null>(null);

  // Seed initial expansion: the org tree is small — open every branch once.
  useEffect(() => {
    if (!autoExpandSeeded && orgs.length > 0) {
      setExpandedKeys(orgs.map((o) => o.id));
      setAutoExpandSeeded(true);
    }
  }, [autoExpandSeeded, orgs]);

  const treeData = useMemo(() => {
    const renderTitle = (org: OrganisationUnit): React.ReactNode => {
      const isActive = org.status === 'active';
      const origin = isActive ? scopeOriginOf(org, byId) : null;

      return (
        <span
          className="tw-inline-flex tw-items-center tw-gap-2"
          style={{ color: isActive ? token.colorText : token.colorTextQuaternary }}
        >
          <span style={{ fontWeight: org.depth === 1 ? token.fontWeightStrong : undefined }}>
            {org.name}
          </span>
          <Text
            type="secondary"
            style={{
              fontSize: token.fontSizeSM,
              color: isActive ? undefined : token.colorTextQuaternary,
            }}
          >
            {org.code}
          </Text>
          {/* 直營/加盟 operating-model badge (flat-table parity). */}
          <Tag color={org.model === 'franchise' ? 'gold' : 'blue'} bordered={false}>
            {t(`model.${org.model}`)}
          </Tag>
          {!isActive && <Tag bordered={false}>{t('status.inactive')}</Tag>}
          {/* Scope-boundary flag — glyph + full-word label + own tooltip
              (focusable: the tooltip reveals on keyboard focus, not hover-only). */}
          {isActive && org.model === 'franchise' && (
            <Tooltip title={t('org.franchiseBoundaryTooltip')} trigger={['hover', 'focus']}>
              <Tag
                tabIndex={0}
                color="orange"
                icon={<PartitionOutlined />}
                style={{ marginInlineEnd: 0 }}
              >
                {t('org.franchiseBoundary')}
              </Tag>
            </Tooltip>
          )}
          {/* Inherited-scope indicator — distinct glyph + label + own tooltip
              (focusable, same hover+focus reveal). */}
          {origin && (
            <Tooltip
              title={t('org.tree.inheritedScopeTooltip', { name: origin.name })}
              trigger={['hover', 'focus']}
            >
              <Tag
                tabIndex={0}
                icon={<NodeIndexOutlined />}
                bordered={false}
                style={{
                  marginInlineEnd: 0,
                  color: token.colorTextSecondary,
                  backgroundColor: token.colorFillQuaternary,
                }}
              >
                {t('org.tree.inheritedScope')}
              </Tag>
            </Tooltip>
          )}
          {isActive && (
            <Tooltip title={t('org.tree.addChild')}>
              <Button
                type="text"
                size="small"
                icon={<PlusOutlined style={{ fontSize: token.sizeXS, color: token.colorPrimary }} />}
                aria-label={t('org.tree.addChildTo', { name: org.name })}
                onClick={(event) => {
                  event.stopPropagation();
                  onAddChild(org.id);
                }}
              />
            </Tooltip>
          )}
          <Dropdown
            trigger={['click']}
            menu={{
              items: [
                // Keyboard/SR-reachable re-parent (drag stays the pointer
                // power-path) — same eligibility as `nodeDraggable`.
                ...(isActive
                  ? [
                      {
                        key: 'changeParent',
                        label: t('org.tree.changeParent'),
                        onClick: ({ domEvent }: { domEvent: React.MouseEvent | React.KeyboardEvent }) => {
                          domEvent.stopPropagation();
                          setReparentDraft(org.parentId ?? null);
                          setReparentTarget(org);
                        },
                      },
                    ]
                  : []),
                {
                  key: 'toggle',
                  label: isActive ? t('actions.deactivate') : t('actions.activate'),
                  danger: isActive,
                  style: !isActive ? { color: token.colorSuccess } : undefined,
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation();
                    onToggleStatus(org);
                  },
                },
              ],
            }}
          >
            <Button
              type="text"
              size="small"
              icon={<MoreOutlined />}
              aria-label={t('org.tree.nodeActions', { name: org.name })}
              onClick={(event) => event.stopPropagation()}
            />
          </Dropdown>
        </span>
      );
    };

    const build = (parentId: string | null): TreeDataNode[] =>
      orgs
        .filter((o) => (o.parentId ?? null) === parentId)
        .sort((a, b) => a.code.localeCompare(b.code))
        .map((o) => ({
          key: o.id,
          title: renderTitle(o),
          children: build(o.id),
        }));

    return build(null);
  }, [byId, onAddChild, onToggleStatus, orgs, t, token]);

  /**
   * Eligible new parents for the Change-parent modal — the same guards the
   * drop handler predicts, expressed as FILTERED options (hide-don't-disable):
   * no node from the target's own subtree (cycle), no inactive parent.
   */
  const reparentOptions = useMemo(() => {
    if (!reparentTarget) return [];
    const blocked = orgSubtreeIds(orgs, reparentTarget.id);
    return orgs
      .filter((o) => o.status === 'active' && !blocked.has(o.id))
      .sort((a, b) => a.code.localeCompare(b.code))
      .map((o) => ({ label: `${o.code} · ${o.name}`, value: o.id }));
  }, [orgs, reparentTarget]);

  /** Funnel into the SAME `onMove` intent the drag path uses (host reason gate). */
  const submitReparent = () => {
    if (!reparentTarget) return;
    const currentParentId = reparentTarget.parentId ?? null;
    setReparentTarget(null);
    if (reparentDraft === currentParentId) return; // unchanged — nothing to move
    onMove({ nodeId: reparentTarget.id, newParentId: reparentDraft });
  };

  const handleDrop: TreeProps['onDrop'] = (info) => {
    const dragId = String(info.dragNode.key);
    const dropId = String(info.node.key);
    const dragged = byId.get(dragId);
    const dropNode = byId.get(dropId);
    if (!dragged || !dropNode) return;

    // Drop ONTO a node = become its child; drop into a gap = share its parent.
    const newParentId = !info.dropToGap ? dropNode.id : (dropNode.parentId ?? null);
    if (newParentId === (dragged.parentId ?? null)) return; // org nodes have no reorder

    // Client-side guard prediction (the handler re-checks): cycle + inactive parent.
    if (newParentId !== null) {
      if (orgSubtreeIds(orgs, dragId).has(newParentId)) {
        message.error(t('org.tree.cycleRejected'));
        return;
      }
      const newParent = byId.get(newParentId);
      if (!newParent) return;
      if (newParent.status !== 'active') {
        message.error(t('org.tree.parentInactive'));
        return;
      }
    }

    onMove({ nodeId: dragId, newParentId });
  };

  if (treeData.length === 0) {
    // Loading gates the empty state: it only renders once the fetch has
    // settled empty (same contract as the flag-OFF `<Table loading>`).
    return (
      <div className="tw-py-10 tw-text-center">
        {loading ? <Spin /> : <Text type="secondary">{t('empty.organisations')}</Text>}
      </div>
    );
  }

  return (
    <>
      <Spin spinning={Boolean(loading)}>
        <div style={{ overflowX: 'auto', padding: token.padding }}>
          <Tree
            blockNode
            selectable={false}
            showLine={{ showLeafIcon: false }}
            treeData={treeData}
            expandedKeys={expandedKeys}
            onExpand={(keys) => setExpandedKeys(keys)}
            draggable={{
              icon: false,
              nodeDraggable: (node) => {
                const record = node ? byId.get(String(node.key)) : undefined;
                return record?.status === 'active';
              },
            }}
            onDrop={handleDrop}
          />
        </div>
      </Spin>

      {/* 變更上層 Change-parent modal — keyboard re-parent path, see submitReparent. */}
      <Modal
        open={reparentTarget !== null}
        title={t('org.tree.reparentTitle')}
        okText={t('actions.save')}
        cancelText={t('actions.cancel')}
        onCancel={() => setReparentTarget(null)}
        onOk={submitReparent}
        okButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
        cancelButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
        maskClosable={false}
        destroyOnHidden
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: token.marginSM }}>
          {reparentTarget && (
            <Text type="secondary">{`${reparentTarget.code} · ${reparentTarget.name}`}</Text>
          )}
          <div>
            <Text style={formStyles.labelStyle}>{t('fields.parent')}</Text>
            <Select
              size="large"
              allowClear
              style={{ width: '100%' }}
              placeholder={t('organisations.noParent')}
              value={reparentDraft ?? undefined}
              options={reparentOptions}
              onChange={(value?: string) => setReparentDraft(value ?? null)}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default OrgTreeEditor;
